import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { SmartTeamsAppUser } from '../db/SmartTeamsAppUser.js';
import type { SmartTeamsAppUserGroup } from '../db/SmartTeamsAppUserGroup.js';
import { normalizeEmail } from './email-filter.js';

export interface UpsertAppUserInput {
  /** AdornisUser._id — the only stable join key for SmartTeamsAppUser. */
  userId: string;

  /**
   * Required for both create and update. On update, refusing the call when this differs
   * from the existing record is the central guard against silent privilege flips
   * (e.g. an FE accidentally being relabeled as a Customer).
   */
  userGroup: SmartTeamsAppUserGroup;

  /** Optional fields — only `$set` when explicitly provided (undefined = "leave as-is"). */
  email?: string;
  username?: string;
  primaryLanguage?: string;
  /** FE-specific. */
  serviceNowFEId?: string;
  /** FE-specific. `null` clears it (the FE was removed from their company); `undefined` leaves it as-is. */
  companyId?: string | null;
  /** Customer-specific. */
  customerIds?: string[];
  archived?: boolean;
}

/**
 * Single source of truth for SmartTeamsAppUser create-or-update. Three callers depend on
 * it: setUserCustomerAssignments, createCustomerWithUser, and ensureAppUserForFieldEngineer.
 *
 * Behaviour:
 * - First lookup is by `userId`. There is no unique index on this field today, so two
 *   concurrent first-time calls can race; that's a known follow-up (atomic findOneAndUpdate
 *   + index migration).
 * - On update, refuses with a clear error if the existing record's `userGroup` differs from
 *   the requested one. This is the safety net against the deep-review's "silent userGroup
 *   flip" finding — admins must explicitly archive/remove a user before re-onboarding them
 *   into a different group.
 * - On update, only fields explicitly present on `input` are written. Existing values for
 *   omitted fields are preserved.
 */
export async function upsertSmartTeamsAppUser(input: UpsertAppUserInput): Promise<SmartTeamsAppUser> {
  const { userId, userGroup } = input;
  if (!userId) throw new Error('upsertSmartTeamsAppUser: userId is required');
  if (!userGroup) throw new Error('upsertSmartTeamsAppUser: userGroup is required');

  // This is the single choke point for all SmartTeamsAppUser writes (including the raw update
  // below, which bypasses entity hooks), so canonicalize the email to lowercase here.
  const email = input.email !== undefined ? normalizeEmail(input.email) : undefined;

  const col = await getRawCollection<SmartTeamsAppUser>(SmartTeamsAppUser._collectionName);
  const existing = await col.findOne({ userId });
  const now = new Date();

  if (existing) {
    if (existing.userGroup && existing.userGroup !== userGroup) {
      throw new Error(
        `User ${userId} is already onboarded as ${existing.userGroup}; refusing to convert to ${userGroup}. ` +
          `Archive or remove the existing app-user record first.`,
      );
    }

    const patch: Partial<SmartTeamsAppUser> = { updatedAt: now };
    // Only set userGroup when missing — avoids no-op writes when it's already correct.
    if (!existing.userGroup) patch.userGroup = userGroup;
    if (email !== undefined) patch.email = email;
    if (input.username !== undefined) patch.username = input.username;
    if (input.primaryLanguage !== undefined) patch.primaryLanguage = input.primaryLanguage;
    if (input.serviceNowFEId !== undefined) patch.serviceNowFEId = input.serviceNowFEId;
    if (input.companyId !== undefined) patch.companyId = input.companyId;
    if (input.customerIds !== undefined) patch.customerIds = input.customerIds;
    if (input.archived !== undefined) patch.archived = input.archived;

    await col.updateOne({ _id: existing._id }, { $set: patch });
  } else {
    const fresh = new SmartTeamsAppUser({
      userId,
      userGroup,
      username: input.username ?? email ?? userId,
      email,
      primaryLanguage: input.primaryLanguage,
      serviceNowFEId: input.serviceNowFEId,
      companyId: input.companyId,
      customerIds: input.customerIds ?? [],
      archived: input.archived ?? false,
      createdAt: now,
      updatedAt: now,
    });
    await fresh.create();
  }

  const result = await col.findOne({ userId });
  return new SmartTeamsAppUser(result!);
}
